// 051 — Historia Clínica Materna Perinatal (Fase 4, Tanda 1a, ver PLAN.md
// "FASE 4 — spec completa"). "Adaptado de la Historia Clínica Perinatal Base
// CLAP-OPS/OMS" (páginas 178-179 del PDF oficial) — a diferencia del resto
// del Anexo 1 MSP, este formulario no numera sus secciones con letras; los
// nombres de bloque de acá (identificacion/antecedentes/gestacionActual/
// controlPrenatal) son descriptivos, no oficiales.
//
// Migra 051 de Patrón A (`051.json`, 16 ítems) a Patrón B bespoke — mismo
// criterio que 008/024: el contenido real (>250 variables entre las 2
// páginas) no cabe en el motor genérico de Questionnaire sin colapsar todo
// a texto libre. Esta tanda cubre la página 1 salvo Parto/Postparto/
// Morbilidad/Intervenciones (Tanda 1b) y el Partograma (Tanda 2, página 2).
//
// Decisión de diseño: `gestas/partos/abortos/cesareas/fum/fpp` preservan
// exactamente el mecanismo FHIR ya existente (`extraerHistoriaMaternoPerinatal`
// en extractors.ts, extensión `EXT_DATOS_GESTACIONALES` del Patient) — no se
// reinventa, solo se migra el origen del dato de un `Questionnaire` a este
// estado tipado. Altura uterina/FCF de "control prenatal" también preservan
// su `Observation` existente. El resto de esta tanda (>60 campos) es
// `Par[]` de respaldo en el PDF firmado, sin recurso FHIR nuevo — mismo
// criterio ya aplicado en 018A/019/008: solo se crea un recurso donde ya
// existe o se anticipa un consumidor clínico real.
import type { Par } from './index';
import { leerSiNo, textoSiNo, type SiNoValor } from './camposSimplesDatos';
import { estadoInicialGestacionActual051, paresDeGestacionActual051, type EstadoGestacionActual051 } from './bloques051GestacionDatos';
import { estadoInicialControlPrenatal051, paresDeControlesPrenatales051, type EstadoControlesPrenatales051 } from './bloques051ControlesDatos';
import type { Traducir } from './tecnicaAnestesicaDatos';

function si(t: Traducir, v: boolean): string {
  return v ? t('formulario.si') : '';
}

/** 'Sí' / 'No' / '' — el papel imprime NO y SI: un "No" contestado debe llegar al PDF firmado. */
function siNo(t: Traducir, v: SiNoValor): string {
  return textoSiNo(v, { si: t('formulario.si'), no: t('formulario.no') });
}

function opcion(t: Traducir, prefijo: string, valor: string): string {
  return valor ? t(`${prefijo}.${valor}`) : '';
}

// ---------- Datos generales (fuera de lo ya cubierto por EncabezadoMspCampos/Patient) ----------

export const AUTOIDENTIFICACION_OPCIONES = ['indigena', 'afroecuatorianaAfrodescendiente', 'negra', 'mulata', 'montubia', 'mestiza', 'blanca', 'otra'] as const;
export type Autoidentificacion = (typeof AUTOIDENTIFICACION_OPCIONES)[number];

export const NACIONALIDAD_MATERNA_OPCIONES = ['ecuatoriana', 'extranjera'] as const;
export type NacionalidadMaterna = (typeof NACIONALIDAD_MATERNA_OPCIONES)[number];

export const ESTADO_CIVIL_051_OPCIONES = ['soltera', 'casada', 'divorciada', 'viuda', 'union', 'unionHecho'] as const;
export type EstadoCivil051 = (typeof ESTADO_CIVIL_051_OPCIONES)[number];

export const NIVEL_EDUCACION_051_OPCIONES = ['ninguna', 'basicaJuvenilAdultos', 'basicaPreparatoria', 'basicaElementalMedia', 'basicaSuperior', 'bachillerato', 'tecnicoSuperior', 'universitario', 'posgrado'] as const;
export type NivelEducacion051 = (typeof NIVEL_EDUCACION_051_OPCIONES)[number];

export interface EstadoDatosGenerales051 {
  readonly telefono: string;
  readonly autoidentificacion: Autoidentificacion | '';
  readonly nacionalidad: NacionalidadMaterna | '';
  readonly nacionalidadCual: string;
  readonly provincia: string;
  readonly canton: string;
  readonly parroquia: string;
  readonly domicilio: string;
  readonly estadoCivil: EstadoCivil051 | '';
  readonly sabeEscribir: boolean;
  readonly nivelEducacion: NivelEducacion051 | '';
  readonly establecimientoLugarParto: string;
}

export function estadoInicialDatosGenerales051(): EstadoDatosGenerales051 {
  return {
    telefono: '',
    autoidentificacion: '',
    nacionalidad: '',
    nacionalidadCual: '',
    provincia: '',
    canton: '',
    parroquia: '',
    domicilio: '',
    estadoCivil: '',
    sabeEscribir: false,
    nivelEducacion: '',
    establecimientoLugarParto: '',
  };
}

function paresDeDatosGenerales051(t: Traducir, e: EstadoDatosGenerales051): Par[] {
  return [
    { pregunta: t('formularios.f051.datosGenerales.telefono'), respuesta: e.telefono.trim() },
    { pregunta: t('formularios.f051.datosGenerales.autoidentificacion'), respuesta: e.autoidentificacion ? t(`formularios.f051.datosGenerales.autoidentificacionOpciones.${e.autoidentificacion}`) : '' },
    { pregunta: t('formularios.f051.datosGenerales.nacionalidad'), respuesta: e.nacionalidad ? t(`formularios.f051.datosGenerales.nacionalidadOpciones.${e.nacionalidad}`) : '' },
    { pregunta: t('formularios.f051.datosGenerales.nacionalidadCual'), respuesta: e.nacionalidadCual.trim() },
    { pregunta: t('formularios.f051.datosGenerales.provincia'), respuesta: e.provincia.trim() },
    { pregunta: t('formularios.f051.datosGenerales.canton'), respuesta: e.canton.trim() },
    { pregunta: t('formularios.f051.datosGenerales.parroquia'), respuesta: e.parroquia.trim() },
    { pregunta: t('formularios.f051.datosGenerales.domicilio'), respuesta: e.domicilio.trim() },
    { pregunta: t('formularios.f051.datosGenerales.estadoCivil'), respuesta: e.estadoCivil ? t(`formularios.f051.datosGenerales.estadoCivilOpciones.${e.estadoCivil}`) : '' },
    { pregunta: t('formularios.f051.datosGenerales.sabeEscribir'), respuesta: si(t, e.sabeEscribir) },
    { pregunta: t('formularios.f051.datosGenerales.nivelEducacion'), respuesta: e.nivelEducacion ? t(`formularios.f051.datosGenerales.nivelEducacionOpciones.${e.nivelEducacion}`) : '' },
    { pregunta: t('formularios.f051.datosGenerales.establecimientoLugarParto'), respuesta: e.establecimientoLugarParto.trim() },
  ];
}

// ---------- Antecedentes familiares y personales (papel: columnas "no / si" por ítem) ----------
//
// Parte (b) de la reestructuración (Ola 2). El papel trae, por ítem, dos círculos "no" / "si": cada uno se contesta con
// `SiNoBotones` (Sí / No / sin responder) y el "No" llega al `Par[]` firmado. Este formulario solo escribe el `Par[]`
// (no relee lo guardado), así que el cambio de modelo no requiere migrar nada.

/** Columna FAMILIARES del papel. Preeclampsia y eclampsia son ítems separados. */
export const ANTECEDENTES_FAMILIARES_OPCIONES = ['tbc', 'diabetes', 'hipertension', 'preeclampsia', 'eclampsia', 'otraCondicionGrave'] as const;

/** Columna PERSONALES del papel: los seis primeros repiten a los familiares y luego cirugía génito-urinaria … VIH. */
export const ANTECEDENTES_PERSONALES_OPCIONES = [
  'tbc',
  'diabetes',
  'hipertension',
  'preeclampsia',
  'eclampsia',
  'otraCondicionGrave',
  'cirugiaGenitoUrinaria',
  'infertilidad',
  'cardiopatia',
  'nefropatia',
  'violencia',
  'vih',
] as const;

/** Diabetes personal: tipo I, II o G (el papel imprime "I II G"; G = gestacional en el CLAP). */
export const DIABETES_TIPO_OPCIONES = ['I', 'II', 'G'] as const;
export type DiabetesTipo = (typeof DIABETES_TIPO_OPCIONES)[number];

export interface EstadoAntecedentesFamiliares051 {
  readonly presentes: Readonly<Record<string, SiNoValor>>;
}

export function estadoInicialAntecedentesFamiliares051(): EstadoAntecedentesFamiliares051 {
  return { presentes: {} };
}

export interface EstadoAntecedentesPersonales051 {
  readonly presentes: Readonly<Record<string, SiNoValor>>;
  readonly diabetesTipo: DiabetesTipo | '';
  /** El papel trae una sola línea "Cuál?" para la otra condición médica grave (familiar y personal). */
  readonly otraCual: string;
}

export function estadoInicialAntecedentesPersonales051(): EstadoAntecedentesPersonales051 {
  return { presentes: {}, diabetesTipo: '', otraCual: '' };
}

function paresSiNoPorClave(t: Traducir, prefijoI18n: string, claves: readonly string[], valores: Readonly<Record<string, SiNoValor>>): Par[] {
  return claves.map((c) => ({ pregunta: `${t(`${prefijoI18n}.titulo`)} — ${t(`${prefijoI18n}.opciones.${c}`)}`, respuesta: siNo(t, leerSiNo(valores[c])) }));
}

/** Familiares + personales. La línea "Cuál?" y el tipo de diabetes solo se imprimen si su padre está en Sí (no hay hijos huérfanos). */
export function paresDeAntecedentesFamiliaresPersonales051(t: Traducir, fam: EstadoAntecedentesFamiliares051, per: EstadoAntecedentesPersonales051): Par[] {
  const otraSi = leerSiNo(fam.presentes.otraCondicionGrave) === 'si' || leerSiNo(per.presentes.otraCondicionGrave) === 'si';
  const diabetesSi = leerSiNo(per.presentes.diabetes) === 'si';
  const pares: Par[] = [
    ...paresSiNoPorClave(t, 'formularios.f051.antecedentesFamiliares', ANTECEDENTES_FAMILIARES_OPCIONES, fam.presentes),
    ...paresSiNoPorClave(t, 'formularios.f051.antecedentesPersonales', ANTECEDENTES_PERSONALES_OPCIONES, per.presentes),
  ];
  if (diabetesSi && per.diabetesTipo) pares.push({ pregunta: t('formularios.f051.antecedentesPersonales.diabetesTipo'), respuesta: t(`formularios.f051.antecedentesPersonales.diabetesTipoOpciones.${per.diabetesTipo}`) });
  if (otraSi) pares.push({ pregunta: t('formularios.f051.antecedentesPersonales.otraCual'), respuesta: per.otraCual.trim() });
  return pares;
}

// ---------- Antecedentes obstétricos ----------

/** Último embarazo: n/a, normal, < 2500 g o > 4000 g (cuatro círculos en el papel). */
export const ULTIMO_EMBARAZO_OPCIONES = ['noAplica', 'normal', 'menor2500g', 'mayor4000g'] as const;
export type UltimoEmbarazo = (typeof ULTIMO_EMBARAZO_OPCIONES)[number];

/** Fin del embarazo anterior: círculo "menos de 1 año" o "n/a" (además de la fecha día/mes/año). */
export const FIN_EMBARAZO_ANTERIOR_OPCIONES = ['menosDeUnAnio', 'noAplica'] as const;
export type FinEmbarazoAnterior = (typeof FIN_EMBARAZO_ANTERIOR_OPCIONES)[number];

export const FRACASO_METODO_ANTICONCEPTIVO_OPCIONES = ['barrera', 'diu', 'hormonales', 'natural', 'quirurgico', 'noUsaba'] as const;
export type FracasoMetodoAnticonceptivo = (typeof FRACASO_METODO_ANTICONCEPTIVO_OPCIONES)[number];

/** Contadores del diagrama OBSTÉTRICOS del papel, en el orden en que se imprimen. */
export const CONTADORES_OBSTETRICOS = [
  'gestas',
  'abortos',
  'partos',
  'vaginales',
  'cesareas',
  'nacidosVivos',
  'nacidosMuertos',
  'viven',
  'muertos1raSemana',
  'muertosDespues1raSemana',
  'embarazoMolar',
  'embarazoEctopico',
] as const;
export type ContadorObstetrico = (typeof CONTADORES_OBSTETRICOS)[number];

export interface EstadoAntecedentesObstetricos051 {
  readonly gestas: string;
  readonly abortos: string;
  readonly partos: string;
  readonly vaginales: string;
  readonly cesareas: string;
  readonly nacidosVivos: string;
  readonly nacidosMuertos: string;
  readonly viven: string;
  readonly muertos1raSemana: string;
  readonly muertosDespues1raSemana: string;
  readonly embarazoMolar: string;
  readonly embarazoEctopico: string;
  /** Un solo círculo en el papel ("3 espont. consecutivos"): marcar / no marcar, no es Sí/No. Cuelga de los abortos. */
  readonly tresEspontaneosConsecutivos: boolean;
  readonly fechaUltimaMenstruacion: string;
  readonly fechaProbableParto: string;
  readonly ultimoEmbarazo: UltimoEmbarazo | '';
  readonly embarazosMultiples: SiNoValor;
  readonly finEmbarazoAnterior: FinEmbarazoAnterior | '';
  readonly finEmbarazoAnteriorFecha: string;
  readonly embarazoPlanificado: SiNoValor;
  readonly fracasoMetodoAnticonceptivo: FracasoMetodoAnticonceptivo | '';
}

export function estadoInicialAntecedentesObstetricos051(): EstadoAntecedentesObstetricos051 {
  return {
    gestas: '',
    abortos: '',
    partos: '',
    vaginales: '',
    cesareas: '',
    nacidosVivos: '',
    nacidosMuertos: '',
    viven: '',
    muertos1raSemana: '',
    muertosDespues1raSemana: '',
    embarazoMolar: '',
    embarazoEctopico: '',
    tresEspontaneosConsecutivos: false,
    fechaUltimaMenstruacion: '',
    fechaProbableParto: '',
    ultimoEmbarazo: '',
    embarazosMultiples: '',
    finEmbarazoAnterior: '',
    finEmbarazoAnteriorFecha: '',
    embarazoPlanificado: '',
    fracasoMetodoAnticonceptivo: '',
  };
}

/** ¿Hay al menos un aborto anotado? De eso depende "3 espont. consecutivos". */
export function hayAbortos051(e: { readonly abortos: string }): boolean {
  const n = Number(e.abortos.trim());
  return e.abortos.trim() !== '' && Number.isFinite(n) && n > 0;
}

function paresDeAntecedentesObstetricos051(t: Traducir, e: EstadoAntecedentesObstetricos051): Par[] {
  const p = 'formularios.f051.antecedentesObstetricos';
  const pares: Par[] = CONTADORES_OBSTETRICOS.map((c) => ({ pregunta: t(`${p}.${c}`), respuesta: e[c].trim() }));
  if (e.tresEspontaneosConsecutivos && hayAbortos051(e)) pares.push({ pregunta: t(`${p}.tresEspontaneosConsecutivos`), respuesta: t('formulario.si') });
  pares.push(
    { pregunta: t(`${p}.fechaUltimaMenstruacion`), respuesta: e.fechaUltimaMenstruacion.trim() },
    { pregunta: t(`${p}.fechaProbableParto`), respuesta: e.fechaProbableParto.trim() },
    { pregunta: t(`${p}.ultimoEmbarazo`), respuesta: opcion(t, `${p}.ultimoEmbarazoOpciones`, e.ultimoEmbarazo) },
    { pregunta: t(`${p}.embarazosMultiples`), respuesta: siNo(t, e.embarazosMultiples) },
    { pregunta: t(`${p}.finEmbarazoAnterior`), respuesta: opcion(t, `${p}.finEmbarazoAnteriorOpciones`, e.finEmbarazoAnterior) },
    // "n/a" = no hubo embarazo anterior: una fecha suelta quedaría huérfana.
    { pregunta: t(`${p}.finEmbarazoAnteriorFecha`), respuesta: e.finEmbarazoAnterior === 'noAplica' ? '' : e.finEmbarazoAnteriorFecha.trim() },
    { pregunta: t(`${p}.embarazoPlanificado`), respuesta: siNo(t, e.embarazoPlanificado) },
    { pregunta: t(`${p}.fracasoMetodoAnticonceptivo`), respuesta: opcion(t, `${p}.fracasoMetodoAnticonceptivoOpciones`, e.fracasoMetodoAnticonceptivo) }
  );
  return pares;
}

// ---------- Estado combinado ----------

export interface EstadoIdentificacion051 {
  readonly datosGenerales: EstadoDatosGenerales051;
  readonly antecedentesFamiliares: EstadoAntecedentesFamiliares051;
  readonly antecedentesPersonales: EstadoAntecedentesPersonales051;
  readonly antecedentesObstetricos: EstadoAntecedentesObstetricos051;
  readonly gestacionActual: EstadoGestacionActual051;
  readonly controlPrenatal: EstadoControlesPrenatales051;
}

export function estadoInicialIdentificacion051(): EstadoIdentificacion051 {
  return {
    datosGenerales: estadoInicialDatosGenerales051(),
    antecedentesFamiliares: estadoInicialAntecedentesFamiliares051(),
    antecedentesPersonales: estadoInicialAntecedentesPersonales051(),
    antecedentesObstetricos: estadoInicialAntecedentesObstetricos051(),
    gestacionActual: estadoInicialGestacionActual051(),
    controlPrenatal: estadoInicialControlPrenatal051(),
  };
}

/** Serializa todo el bloque de identificación a `Par[]`, listos para `onGuardado` — filtra respuestas vacías. */
export function paresDeIdentificacion051(t: Traducir, estado: EstadoIdentificacion051): Par[] {
  const pares: Par[] = [
    ...paresDeDatosGenerales051(t, estado.datosGenerales),
    ...paresDeAntecedentesFamiliaresPersonales051(t, estado.antecedentesFamiliares, estado.antecedentesPersonales),
    ...paresDeAntecedentesObstetricos051(t, estado.antecedentesObstetricos),
    ...paresDeGestacionActual051(t, estado.gestacionActual),
    ...paresDeControlesPrenatales051(t, estado.controlPrenatal),
  ];
  return pares.filter((p) => p.respuesta.trim() !== '');
}
